import * as Sentry from "@sentry/browser";

// Anchored at the start of the URL and closed with a path separator. Without
// the anchor these match anywhere in the string, so any host could carry one
// of these domains in a query parameter and have its errors dropped
// (CodeQL js/regex/missing-regexp-anchor).
const DENY_URLS = [
  /^https?:\/\/[a-zA-Z0-9-]+\.translate\.goog\//,
  /^https:\/\/app\.convertful\.com\//,
  /^https:\/\/monitor\.clickcease\.com\//,
  /^https:\/\/consentcdn\.cookiebot\.com\//,
  /^https:\/\/www\.googletagmanager\.com\//,
  /^https:\/\/[a-zA-Z0-9]*\.clarity\.ms\//,
  /^https:\/\/www\.clickcease\.com\//,
  /^https:\/\/region1\.analytics\.google\.com\//,
  /^https:\/\/sleeknotestaticcontent\.sleeknote\.com\//,
];

// About this ignored error:
// https://github.com/lavendla/website/pull/589
// https://github.com/getsentry/sentry/issues/61469#issuecomment-1891914331
const IGNORE_ERRORS = [
  /(evaluating 'Object\.prototype\.hasOwnProperty\.call\(e,"telephone")\)/,
  // Network connectivity errors
  /Failed to fetch/,
  /Load failed/,
  /NetworkError/,
  // Google Maps warnings
  /google\.maps\.Marker is deprecated/,
  /AdvancedMarkerElement/,
  /NoApiKeys/,
  /Google Maps JavaScript API warning/,
  /Google Maps JavaScript API could not load/,
  /Could not load "places_impl"/,
  // Google Maps map-id warnings (WEBSITE-14X, WEBSITE-14Y, WEBSITE-15T).
  // Google logs these from its own script when the map-id config request
  // does not complete — over 90% of them are AdsBot-Google, whose renderer
  // never finishes the request. Real users initialise maps fine, so the map
  // id itself is valid and there is nothing to fix on our side.
  // The Maps API is loaded in each site's language, so every translation
  // groups as a separate Sentry issue; add locales as they show up.
  /Unable to fetch configuration for mapId/,
  /without a valid Map ID/,
  /utan ett giltigt kart-id/,
  // Variable reference errors
  /memorialVars is not defined/,
  /Can't find variable: memorialVars/,
  // Browser caching warnings
  /you cached a dynamic script/,
  /operation degraded/,
  // JSON parsing errors
  /JSON\.parse: unexpected character/,
  /SyntaxError: Unexpected EOF/,
  /SyntaxError: Invalid or unexpected token/,
  // Google Analytics warnings
  /Google Analytics for WooCommerce/,
  /Configuration and tracking data not found/,
  // RPC errors
  /Method doesn't allow unregistered callers/,
  /RpcError/,
  // Third-party noise
  /Multiple Clarity tags detected/,
  /Duplicated clickcease/,
  /Double Drip script detected/,
  // Google Translate errors (WEBSITE-N7) — originate from translate_http script, not our code
  /translate_http/,
  // Facebook/Instagram in-app browser bridge errors (WEBSITE-Y0, WEBSITE-TE,
  // WEBSITE-YD, WEBSITE-E). The Android WebView injects iabjs:// scripts
  // that postMessage metrics to the native app on page exit; when the Java
  // side of the bridge is already torn down the call throws. Every event is
  // Facebook or Instagram on Android and nothing user-facing fails.
  /Java object is gone/,
  /Java bridge method invocation error/,
  // Redux DevTools extension noise (WEBSITE-50). @redux-devtools/instrument
  // emits this from inside the extension itself; we ship no redux and nothing
  // on the page polyfills Symbol.observable, so there is nothing to fix here.
  /Symbol\.observable as defined by Redux/,
];

const FAILED_REQUEST_TARGETS = [/^(?!.*\/wp-json(\/|$|\?))/];

function initSentry() {
  const dsn = window.sentryConfig?.sentryDsn;
  const environment = window.sentryConfig?.sentryEnv;
  const replaysAreEnabled = window.sentryConfig?.sentryReplaysAreEnabled;

  if (!dsn) {
    console.warn("Sentry JS logging not active");
    return;
  }

  const beforeSend = (event, hint) => {
    let gtm = false;

    try {
      // Check if the exception stack contains GTM
      gtm = hint?.originalException?.stack
        ? hint.originalException.stack.indexOf(
            "www.googletagmanager.com/gtm.js?id="
          ) > -1
        : false;
    } catch (error) {
      console.warn("Error while checking for GTM", error);
    }

    // Filter out common useless errors
    if (event.exception?.values) {
      for (const exception of event.exception.values) {
        const errorMessage = exception.value || "";

        // Filter out network connectivity errors
        if (
          errorMessage.includes("Failed to fetch") ||
          errorMessage.includes("Load failed") ||
          errorMessage.includes("NetworkError") ||
          errorMessage.includes("TypeError: Failed to fetch") ||
          errorMessage.includes("TypeError: Load failed")
        ) {
          return null;
        }

        // Filter out Google Maps deprecation warnings
        if (
          errorMessage.includes("google.maps.Marker is deprecated") ||
          errorMessage.includes("AdvancedMarkerElement") ||
          errorMessage.includes("NoApiKeys") ||
          errorMessage.includes("Google Maps JavaScript API warning")
        ) {
          return null;
        }

        // Filter out variable reference errors
        if (
          errorMessage.includes("memorialVars is not defined") ||
          errorMessage.includes("Can't find variable: memorialVars") ||
          errorMessage.includes("memorialVars")
        ) {
          return null;
        }

        // Filter out browser caching warnings
        if (
          errorMessage.includes("you cached a dynamic script") ||
          errorMessage.includes("operation degraded")
        ) {
          return null;
        }

        // Filter out JSON parsing errors from external sources
        if (
          errorMessage.includes("JSON.parse: unexpected character") ||
          errorMessage.includes("SyntaxError: Unexpected EOF") ||
          errorMessage.includes("SyntaxError: Invalid or unexpected token")
        ) {
          return null;
        }

        // Filter out Google Analytics warnings
        if (
          errorMessage.includes("Google Analytics for WooCommerce") ||
          errorMessage.includes("Configuration and tracking data not found")
        ) {
          return null;
        }

        // Filter out RPC errors from external services
        if (
          errorMessage.includes("Method doesn't allow unregistered callers") ||
          errorMessage.includes("RpcError")
        ) {
          return null;
        }

        // Filter out Cookiebot cookie consent errors (WEBSITE-5)
        if (
          errorMessage.includes("Illegal invocation") &&
          hint?.originalException?.stack?.includes("uc.js")
        ) {
          return null;
        }

        // Filter out Google Translate errors (WEBSITE-N7)
        // These originate entirely from Google's own translate_http script and cannot be fixed by us
        if (hint?.originalException?.stack?.includes("translate_http")) {
          return null;
        }
      }
    }

    // Filter out specific error messages
    if (event.message) {
      const message = event.message.toLowerCase();

      if (
        message.includes("failed to fetch") ||
        message.includes("load failed") ||
        message.includes("networkerror") ||
        message.includes("noapikeys") ||
        message.includes("memorialvars") ||
        message.includes("you cached a dynamic script") ||
        message.includes("google maps javascript api warning") ||
        message.includes("google analytics for woocommerce") ||
        message.includes("kunne ikke oprette assembly") ||
        message.includes("could not create assembly")
      ) {
        return null;
      }
    }

    event.tags = event.tags || {};
    event.extra = event.extra || {};

    if (gtm) {
      event.tags.logger = "gtm";
      event.extra.dataLayer = dataLayer.map((i) => {
        try {
          return JSON.stringify(i, null, " ");
        } catch (error) {
          return `[cannot be serialized]: ${error.message}`;
        }
      });
    }

    event.tags.logger = event.tags.logger || "_default_";
    return event;
  };

  Sentry.init({
    beforeSend,
    denyUrls: DENY_URLS,
    dsn,
    environment,
    ignoreErrors: IGNORE_ERRORS,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["error", "warn"],
      }),
      Sentry.httpClientIntegration({
        failedRequestTargets: FAILED_REQUEST_TARGETS,
      }),
      ...(replaysAreEnabled
        ? [
            Sentry.replayIntegration({
              blockAllMedia: false,
              maskAllText: false,
            }),
          ]
        : []),
    ],
    release: window.sentryConfig?.sentryRelease,
    replaysOnErrorSampleRate: 0.5,
    replaysSessionSampleRate: 0.1,
    sendDefaultPii: true,
    tracesSampleRate: 0.5,
  });
}

initSentry();
